// @flow
import type {Element} from 'react'
import React, {useCallback, useEffect, useRef, useMemo, useState} from 'react'
import ReactSelect from 'react-select'
import $ from 'jquery'
import util from 'utils/MiscUtils'

type ImageData = {
  fa_icon?: string,
  css_classes?: string,
  image?: string,
}

type Option = {
  value: string,
  label: string,
  subtitle?: string,
  imageData?: ImageData,
};

type PlaceholderData = {
  label: string,
  imageData?: ImageData,
}

type SyntheticImageEvent = {
  currentTarget: HTMLImageElement,
}

export type Props = $ReadOnly<{
  defaultValue?: Object,
  id: string,
  isMulti?: boolean,
  isSearchable?: boolean,
  isClearable?: boolean,
  name: string,
  options: Array<Option>,
  placeholderData?: PlaceholderData,
  wrapperClass?: string,
  iconOnlySelection?: boolean,
}>;

function RailsSelect({defaultValue, id, isMulti, isSearchable, isClearable, name, options, placeholderData, wrapperClass, iconOnlySelection}: Props): Element<'div'> {
  const wrapperRef = useRef(null)
  const selectRef = useRef(null)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [changeInProgress, setChangeInProgress] = useState(false)
  const hasImageDataInOptions = options.some((option) => option.imageData)

  // Hack for bubbling 'change' events:
  // focus and blur events naturally bubble from the underlying ReactSelect component.
  // default/native change events do not bubble, so we have to manually (re-) dispatch them with {bubbles: true} here.
  // setting handlers on for these events has tricks:
  // $form.on('change', 'input, select')  -> will not work. the synthetic event comes from a div
  // $form.on('change', 'div') -> will work but will get an event per div that is bubbled through.
  // $form.on('change', 'div.js-slick-auto-save') -> will work correctly, assuming that exactly one div has that class.
  const handleChange = useCallback(() => {
    setChangeInProgress(true)

    if (wrapperRef.current)
      wrapperRef.current.dispatchEvent(new Event('change', {bubbles: true, cancelable: true}))
    else
      util.warn('wrapperRef.current is null')
  }, [wrapperRef])

  useEffect(() => {
    if (selectRef.current) {
      const availableIds = options.map((option) => option.value)
      const selectedIds = selectRef.current.getValue().map((option) => option.value)

      if (options.length === 1)
        selectRef.current.setValue(options)
      else if (selectedIds.filter((optionId) => availableIds.includes(optionId)).length === 0)
        selectRef.current.setValue(null)
    }
  }, [selectRef, options])

  useEffect(() => {
    if (!iconOnlySelection)
      return undefined

    const tooltipSelector = '.rails_select--option.js-with-tooltip'
    const imagesWithUrls = document.querySelectorAll(`${tooltipSelector} img`)
    const selectWrapper = wrapperRef.current

    const handleImageLoad = (event: SyntheticImageEvent | Event) => {
      const imageElement = (event.currentTarget: any)
      if (imageElement instanceof HTMLImageElement) {
        const parentContainer = imageElement.closest(tooltipSelector)
        if (parentContainer)
          $(parentContainer).tooltip()
      }
    }

    // Make sure tooltips are ready to go when the image loads if it hasn't loaded yet
    if (imagesWithUrls.length > 0) {
      imagesWithUrls.forEach((img) => {
        const imageElement = ((img: any): HTMLImageElement)
        if (imageElement.complete)
          handleImageLoad({currentTarget: imageElement})
        else
          imageElement.addEventListener('load', handleImageLoad)
      })
    }

    const showTooltip = (event) => {
      if (!menuIsOpen && !changeInProgress)
        $(event.currentTarget).tooltip('show')
    }

    const hideTooltip = (event) => {
      $(event.currentTarget).tooltip('hide')
    }

    // Attach hover and focus events to the single value icon
    $(tooltipSelector).on('mouseenter focus', showTooltip)
    $(tooltipSelector).on('mouseleave blur', hideTooltip)

    // Hide tooltip when the menu is opened or input is focused
    if (selectWrapper) {
      $(selectWrapper).on('mousedown', () => {
        $(tooltipSelector).tooltip('hide')
      })
    }

    return () => {
      // Cleanup tooltip event listeners
      $(tooltipSelector).off('mouseenter focus', showTooltip)
      $(tooltipSelector).off('mouseleave blur', hideTooltip)

      if (selectWrapper)
        $(selectWrapper).off('mousedown')
    }
  }, [iconOnlySelection, options, menuIsOpen, changeInProgress])

  const optionImage = useCallback((imageData) => imageData && (
    <div className={`rails_select--option-img ${imageData.css_classes}`}>
      {imageData.fa_icon ? (
        <i className={`fa ${imageData.fa_icon} icon-element`} />
      ) : (
        <img src={imageData.image} alt="" />
      )}
    </div>
  ), [])

  const optionTitleAndSubtitle = useCallback((option) => (
    <React.Fragment>
      <span className="rails_select--option-title">{option.label}</span>
      {option.subtitle && <div className="rails_select--option-subtitle">{option.subtitle}</div>}
    </React.Fragment>
  ), [])

  const formatOptionLabel = useCallback((option) => (
    <span className="rails_select--option">
      {hasImageDataInOptions && optionImage(option.imageData)}
      {optionTitleAndSubtitle(option)}
    </span>
  ), [optionImage, hasImageDataInOptions, optionTitleAndSubtitle])

  const memoizedFormatOptionLabel = useMemo(() => formatOptionLabel, [formatOptionLabel])

  const filterOption = (option, inputValue) => {
    const searchValue = inputValue.toLowerCase()
    const {label, data} = option
    const subtitle = data?.subtitle
    return (
      label.toLowerCase().includes(searchValue)
        || (subtitle?.toLowerCase().includes(searchValue))
    )
  }

  const iconOnlySingleValue = ({data}) => (
    <div className="rails_select__icon-only-single-value">
      <span className="rails_select--option js-with-tooltip" title={data.label}>
        {optionImage(data.imageData)}
      </span>
    </div>
  )

  const customPlaceholder = ({selectProps}) => {
    const {placeholder} = selectProps

    if (iconOnlySelection) {
      return (
        <div className="rails_select__custom-placeholder icon-only-single-value">
          <div className="rails_select--option">
            {optionImage(placeholder.imageData)}
          </div>
        </div>
      )
    }

    return (
      <div className="rails_select__custom-placeholder">
        <div className="rails_select--option">
          {optionImage(placeholder.imageData)}
          <span className="rails_select--option-title">{placeholder.label}</span>
        </div>
      </div>
    )
  }

  const components = {}

  if (iconOnlySelection)
    components.SingleValue = iconOnlySingleValue

  if (placeholderData)
    components.Placeholder = customPlaceholder

  return (
    <div ref={wrapperRef} className={wrapperClass} test-id={`RailsSelect-${name}`}>
      <ReactSelect
        defaultValue={defaultValue}
        id={id}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isClearable={isClearable}
        name={name}
        onChange={handleChange}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        options={options}
        placeholder={placeholderData}
        classNamePrefix="rails_select"
        formatOptionLabel={memoizedFormatOptionLabel}
        filterOption={filterOption}
        components={components}
        // $FlowFixMe[incompatible-type]
        ref={selectRef}
        // defaultMenuIsOpen // Uncomment to open the dropdown by default to inspect things in console more easily
      />
    </div>
  )
}

export default RailsSelect
